import React from 'react';
import './SummaryStats.css';
import Card from '../../../../components/Card/Card';
import Table from '../../../../components/Table/Table';
import { useSelector } from 'react-redux';
import { selectLimitStats, selectTableSizeStats } from '../../selectors';
import type { ShortStatsDto } from '@poker-stats/contracts';
import Text from '../../../../components/Text/Text';
import exhaustiveCheck from '../../../../utils/exhaustiveCheck';
import formatHands from '../../../../utils/formatHands';
import formatWinnings from '../../../../utils/formatWinnings';
import Flex from '../../../../components/Flex/Flex';

function getTextNameOfLimit(limit: 2 | 6 | 9): string {
  switch (limit) {
    case 2:
      return 'HU';
    case 6:
      return 'SH';
    case 9:
      return 'FR';
    default:
      return exhaustiveCheck(limit);
  }
}

const SummaryStats = React.memo(() => {
  const tableSizeStats = useSelector(selectTableSizeStats);
  const limitStats = useSelector(selectLimitStats);
  const hasStats = tableSizeStats.length > 0 || limitStats.length > 0;

  if (!hasStats) {
    return (
      <Card title="Summary">
        <Flex className="SummaryStats__empty" justify="center" align="center">
          <Text>-</Text>
        </Flex>
      </Card>
    );
  }

  return (
    <Card title="Summary">
      <Table
        columns={[
          {
            title: '',
            dataIndex: 'tableSize',
            render: (
              x: ({ limit: number } & ShortStatsDto) | ({ tableSize: 2 | 6 | 9 } & ShortStatsDto),
            ) => {
              if ('limit' in x) {
                return <Text>NL{x.limit}</Text>;
              }

              if ('tableSize' in x) {
                return <Text>{getTextNameOfLimit(x.tableSize)}</Text>;
              }

              return null;
            },
          },
          {
            title: 'Hands',
            dataIndex: 'hands',
            render: (x: ShortStatsDto) => <Text>{formatHands(x.hands, 1)}</Text>,
          },
          {
            title: 'Winnings',
            dataIndex: 'winnings',
            render: (x: ShortStatsDto) => (
              <Text type={x.winnings > 0 ? 'success' : 'error'}>
                {formatWinnings(x.winnings, 1, false)}
              </Text>
            ),
          },
          { title: 'bb/100', dataIndex: 'bb100' },
          { title: 'VPIP', dataIndex: 'vpip' },
          { title: 'PFR', dataIndex: 'pfr' },
          { title: 'WWSF', dataIndex: 'wonwhensawflop' },
        ]}
        data={tableSizeStats.concat(
          limitStats.map((x) => ({
            ...x,
            specialRow: x.winnings === undefined,
          })) as any,
        )}
        spaceIndexes={[tableSizeStats.length - 1]}
        pagination={false}
      />
    </Card>
  );
});

export default SummaryStats;
